import Decimal from 'decimal.js';
import { ExtractedGstInvoice, GstLineItem } from '../types/gst';

/**
 * Official Indian State Codes for GST
 */
export const GST_STATE_CODES: Record<string, string> = {
  "01": "Jammu & Kashmir",
  "02": "Himachal Pradesh",
  "03": "Punjab",
  "04": "Chandigarh",
  "05": "Uttarakhand",
  "06": "Haryana",
  "07": "Delhi",
  "08": "Rajasthan",
  "09": "Uttar Pradesh",
  "10": "Bihar",
  "11": "Sikkim",
  "12": "Arunachal Pradesh",
  "13": "Nagaland",
  "14": "Manipur",
  "15": "Mizoram",
  "16": "Tripura",
  "17": "Meghalaya",
  "18": "Assam",
  "19": "West Bengal",
  "20": "Jharkhand",
  "21": "Odisha",
  "22": "Chhattisgarh",
  "23": "Madhya Pradesh",
  "24": "Gujarat",
  "26": "Dadra & Nagar Haveli and Daman & Diu",
  "27": "Maharashtra",
  "29": "Karnataka",
  "30": "Goa",
  "31": "Lakshadweep",
  "32": "Kerala",
  "33": "Tamil Nadu",
  "34": "Puducherry",
  "35": "Andaman & Nicobar Islands",
  "36": "Telangana",
  "37": "Andhra Pradesh",
  "38": "Ladakh",
  "97": "Other Territory",
  "99": "Centre Jurisdiction / Special"
};

/**
 * Normalizes an invoice number:
 * Uppercase, strip spaces, slashes, dashes, dots, and strip leading zeroes.
 * E.g., "INV/0042" -> "INV42", "2024-25/0001" -> "2024251"
 */
export function normalizeInvoiceNumber(invNum: string | undefined | null): string {
  if (!invNum) return "";
  const cleaned = invNum
    .toString()
    .trim()
    .toUpperCase()
    .replace(/[\s\-_/\\.:]/g, "");
  
  // Strip leading zeroes if it's purely numeric or mixed with prefixes
  return cleaned.replace(/^0+/, "") || cleaned;
}

/**
 * Standard 15-character GSTIN regex validator
 */
const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

/**
 * Validates a 15-character Indian GSTIN
 */
export function validateGstin(gstin: string | undefined | null): {
  isValid: boolean;
  stateCode?: string;
  stateName?: string;
  pan?: string;
  reason?: string;
} {
  if (!gstin) return { isValid: false, reason: "GSTIN is required" };
  const upper = gstin.trim().toUpperCase();

  if (upper.length !== 15) {
    return { isValid: false, reason: `GSTIN must be exactly 15 characters (got ${upper.length})` };
  }

  if (!GSTIN_REGEX.test(upper)) {
    return { isValid: false, reason: "GSTIN format is invalid (Pattern: 2-digit state + 10-char PAN + entity number + Z + checksum)" };
  }

  const stateCode = upper.substring(0, 2);
  const stateName = GST_STATE_CODES[stateCode];
  if (!stateName) {
    return { isValid: false, reason: `Invalid State Code: ${stateCode}` };
  }

  const pan = upper.substring(2, 12);

  // Optional: Checksum calculation (GSTN base 36)
  const isValidChecksum = verifyGstinChecksum(upper);

  return {
    isValid: isValidChecksum,
    stateCode,
    stateName,
    pan,
    reason: isValidChecksum ? undefined : "GSTIN checksum character does not match"
  };
}

/**
 * GSTIN Mod 36 Checksum verification
 */
function verifyGstinChecksum(gstin: string): boolean {
  try {
    const chars = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    let sum = 0;
    for (let i = 0; i < 14; i++) {
      let codePoint = chars.indexOf(gstin[i]);
      if (codePoint === -1) return false;
      const factor = (i % 2 === 0) ? 1 : 2;
      const p = codePoint * factor;
      sum += Math.floor(p / 36) + (p % 36);
    }
    const remainder = sum % 36;
    const checkCodePoint = (36 - remainder) % 36;
    return gstin[14] === chars[checkCodePoint];
  } catch {
    return true; // Fallback to format check if checksum evaluation fails
  }
}

/**
 * Determines whether supply is Intra-state (CGST + SGST) or Inter-state (IGST)
 */
export function determineSupplyTaxType(
  supplierGstin: string,
  buyerGstin: string,
  placeOfSupply?: string
): {
  isInterState: boolean;
  taxType: 'INTRA_STATE' | 'INTER_STATE';
  supplierState: string;
  buyerOrPosState: string;
} {
  const supplierState = supplierGstin ? supplierGstin.substring(0, 2) : "";
  let buyerOrPosState = "";

  if (placeOfSupply) {
    const match = placeOfSupply.match(/\b([0-9]{2})\b/);
    if (match) {
      buyerOrPosState = match[1];
    }
  }
  if (!buyerOrPosState && buyerGstin) {
    buyerOrPosState = buyerGstin.substring(0, 2);
  }

  const isInterState = supplierState !== buyerOrPosState && Boolean(supplierState && buyerOrPosState);
  return {
    isInterState,
    taxType: isInterState ? 'INTER_STATE' : 'INTRA_STATE',
    supplierState,
    buyerOrPosState
  };
}

/**
 * Recalculates line items and totals deterministically using Decimal.js
 * Strictly enforces that LLMs are NEVER used for mathematical addition.
 */
export function validateAndCalculateInvoiceMath(
  invoice: Partial<ExtractedGstInvoice>
): {
  verifiedLineItems: GstLineItem[];
  totalTaxableValue: number;
  totalTax: number;
  calculatedInvoiceTotal: number;
  mathVerified: boolean;
  variance: number;
  normalizedInvoiceNumber: string;
} {
  const lineItems = invoice.line_items || [];
  let totalTaxable = new Decimal(0);
  let totalCgst = new Decimal(0);
  let totalSgst = new Decimal(0);
  let totalIgst = new Decimal(0);
  let totalCess = new Decimal(0);

  const verifiedLineItems: GstLineItem[] = lineItems.map((item) => {
    const qty = new Decimal(item.qty || 1);
    const unitPrice = new Decimal(item.unit_price || 0);

    // If taxable_value was omitted or zero, calculate from qty * unitPrice
    let taxable = new Decimal(item.taxable_value || 0);
    if (taxable.isZero() && !unitPrice.isZero()) {
      taxable = qty.times(unitPrice);
    }

    const cgstRate = new Decimal(item.cgst_rate || 0);
    const sgstRate = new Decimal(item.sgst_rate || 0);
    const igstRate = new Decimal(item.igst_rate || 0);
    const cessRate = new Decimal(item.cess_rate || 0);

    // Deterministic tax calculation
    const cgstAmount = taxable.times(cgstRate).dividedBy(100);
    const sgstAmount = taxable.times(sgstRate).dividedBy(100);
    const igstAmount = taxable.times(igstRate).dividedBy(100);
    const cessAmount = taxable.times(cessRate).dividedBy(100);

    totalTaxable = totalTaxable.plus(taxable);
    totalCgst = totalCgst.plus(cgstAmount);
    totalSgst = totalSgst.plus(sgstAmount);
    totalIgst = totalIgst.plus(igstAmount);
    totalCess = totalCess.plus(cessAmount);

    return {
      description: item.description || "Line Item",
      hsn_sac_code: item.hsn_sac_code || "",
      qty: qty.toNumber(),
      unit_price: unitPrice.toDecimalPlaces(2).toNumber(),
      taxable_value: taxable.toDecimalPlaces(2).toNumber(),
      cgst_rate: cgstRate.toNumber(),
      sgst_rate: sgstRate.toNumber(),
      igst_rate: igstRate.toNumber(),
      cess_rate: cessRate.toNumber(),
      cgst_amount: cgstAmount.toDecimalPlaces(2).toNumber(),
      sgst_amount: sgstAmount.toDecimalPlaces(2).toNumber(),
      igst_amount: igstAmount.toDecimalPlaces(2).toNumber(),
      cess_amount: cessAmount.toDecimalPlaces(2).toNumber(),
    };
  });

  const totalTax = totalCgst.plus(totalSgst).plus(totalIgst).plus(totalCess);
  const calculatedInvoiceTotal = totalTaxable.plus(totalTax);

  const rawExtractedTotal = new Decimal(invoice.invoice_total || 0);
  const variance = calculatedInvoiceTotal.minus(rawExtractedTotal).abs().toNumber();

  // If variance is <= ₹1.00 (standard rounding allowance), math is verified
  // If no raw invoice total was supplied, take calculated total as verified
  const mathVerified = rawExtractedTotal.isZero() ? true : variance <= 1.00;

  const normalizedInvoiceNumber = normalizeInvoiceNumber(invoice.invoice_number);

  return {
    verifiedLineItems,
    totalTaxableValue: totalTaxable.toDecimalPlaces(2).toNumber(),
    totalTax: totalTax.toDecimalPlaces(2).toNumber(),
    calculatedInvoiceTotal: calculatedInvoiceTotal.toDecimalPlaces(2).toNumber(),
    mathVerified,
    variance: Number(variance.toFixed(2)),
    normalizedInvoiceNumber
  };
}
